// Canonical enumerations shared by every Ticketing app and worker.
//
// Each list mirrors a lookup table seeded in migrations/0016_reference_data.sql.
// packages/domain/src/parity.test.ts fails if the two ever diverge, so add a
// value in both places (a migration INSERT plus the entry here) or neither.
import { z } from 'zod';

/** Deployment environment. Dev-only behavior is gated on this everywhere. */
export const APP_ENVIRONMENTS = ['production', 'development'] as const;
export const AppEnvironment = z.enum(APP_ENVIRONMENTS);
export type AppEnvironment = z.infer<typeof AppEnvironment>;

export const ACTOR_TYPES = ['CUSTOMER', 'STAFF', 'RENTER_CONTACT', 'SERVICE', 'DEVICE', 'SYSTEM'] as const;
export const ActorType = z.enum(ACTOR_TYPES);
export type ActorType = z.infer<typeof ActorType>;

export const AUTH_SURFACES = ['STOREFRONT', 'ADMIN', 'BOX_OFFICE', 'SCANNER', 'RENTER_PORTAL'] as const;
export const AuthSurface = z.enum(AUTH_SURFACES);
export type AuthSurface = z.infer<typeof AuthSurface>;

// How a credential was presented. STEP_UP is the signed-in staff member
// re-entering their own PIN; MANAGER_APPROVAL is a second person authorizing
// one action on someone else's device. Keeping them apart is what lets the
// audit trail distinguish self re-authentication from an approval.
export const LOGIN_ATTEMPT_TYPES = ['PIN_LOGIN', 'MANAGER_APPROVAL', 'STEP_UP', 'DEVICE_PAIRING', 'OIDC_CALLBACK', 'MAGIC_LINK'] as const;
export const LoginAttemptType = z.enum(LOGIN_ATTEMPT_TYPES);
export type LoginAttemptType = z.infer<typeof LoginAttemptType>;

// How a session was started (D-370). BRE_AUTH_OIDC is retired but stays: old
// sessions and audit rows were written with it.
export const AUTH_METHODS = ['BRE_AUTH_OIDC', 'PIN', 'GOOGLE_OIDC', 'MAGIC_LINK'] as const;
export const AuthMethod = z.enum(AUTH_METHODS);
export type AuthMethod = z.infer<typeof AuthMethod>;

export const ROLES = [
  'box_office_cashier',
  'box_office_manager',
  'scanner',
  'event_manager',
  'finance',
  'renter_manager',
  'admin',
  // Works group inquiries end to end (D-376).
  'group_sales',
] as const;
export const Role = z.enum(ROLES);
export type Role = z.infer<typeof Role>;

export const PERMISSIONS = [
  'admin.access',
  'migration.import',
  'surveys.manage',
  'house_accounts.manage',
  'settings.manage',
  'audit.view',
  'staff.view',
  'staff.manage',
  'roles.manage',
  'devices.manage',
  'registers.manage',
  'printers.manage',
  'terminals.manage',
  'venues.manage',
  'manifests.edit',
  'manifests.publish',
  'events.view',
  'events.manage',
  'events.publish',
  'events.delete',
  'terms.manage',
  'email_wording.manage',
  'add_ons.manage',
  'assistive_devices.manage',
  'assistive_devices.lend',
  'performances.manage',
  'pricing.manage',
  'pricing.activate',
  'fees.manage',
  'taxes.manage',
  'accounting_treatment.manage',
  'promotions.manage',
  'inventory.view',
  'inventory.hold.place',
  'inventory.hold.release',
  'inventory.hold.convert',
  'inventory.kill',
  'inventory.pool.manage',
  'timed_inventory.view',
  'timed_inventory.manage',
  'timed_inventory.override',
  'events.content.edit',
  'ticket_designs.view',
  'ticket_designs.manage',
  'media.view',
  'media.manage',
  'sponsors.view',
  'sponsors.manage',
  'inventory.preempt_cart',
  'orders.view',
  'orders.search',
  'orders.note',
  'orders.balance_due',
  'orders.payments.collect',
  'orders.payment_requests.manage',
  'orders.cancel_unpaid',
  'orders.contact.update',
  'customers.lookup',
  'customers.create',
  'customers.contact.view',
  // Notes and tags on a customer record (D-358).
  'customers.note',
  'customers.tag',
  'customers.tags.manage',
  /** Add to or take from a customer's account credit, with a reason (D-372). */
  'customers.credit.adjust',
  'box_office.session.open',
  'box_office.session.close',
  'box_office.session.force_close',
  'box_office.sell',
  'box_office.sell_from_hold',
  'box_office.sell_restricted_pool',
  'box_office.discount.standard',
  'box_office.discount.custom',
  'box_office.comp',
  'box_office.manual_card',
  'box_office.check',
  'box_office.house_account',
  'box_office.drawer.open',
  'box_office.drawer.close',
  'box_office.drawer.view_expected',
  'box_office.drawer.correct',
  'box_office.cash_movement',
  'box_office.reprint',
  'box_office.will_call',
  'refunds.request',
  'refunds.approve',
  'refunds.override_fee_policy',
  'exchanges.process',
  'transfers.admin',
  'tickets.void',
  'tickets.reissue',
  'tickets.revoke',
  'tickets.reinstate',
  'scan.admit',
  'scan.override',
  'scan.offline_bundle',
  'scan.configure',
  'reports.view_sales',
  'reports.view_financial',
  'reports.view_inventory',
  'reports.view_attendance',
  'reports.export',
  'reports.schedule.manage',
  'packages.manage',
  // Comp vouchers and season passes (D-373).
  'vouchers.manage',
  'season_passes.manage',
  // Rain a game out (D-374).
  'performances.rain_out',
  'settlements.view',
  'settlements.prepare',
  'settlements.approve',
  'settlements.record_payment',
  'renters.manage',
  'renters.grant_access',
  'finance.reconciliation.view',
  'finance.reconciliation.manage',
  'finance.payouts.sign_off',
  'finance.qbo.manage',
  'finance.accounting_mappings.manage',
  'finance.adjustments.create',
  'donations.view',
  'donations.manage',
  'group_sales.view',
  'group_sales.manage',
  // Group booking management (D-376): price and send a quote; hold a space and book it.
  'group_sales.quote',
  'group_sales.book',
  // The organizations BRE sells to, as records rather than retyped names (D-244).
  'organizations.view',
  'organizations.manage',
  'disputes.view',
  'disputes.manage',
  'dev_tools.use',
  // The team's connected Stripe account (Stripe Connect P2, D-393).
  'payments.stripe_account.view',
  'payments.stripe_account.manage',
  // STGD-only (D-393): reserved, held by no role, given only to PLATFORM_STAFF_EMAILS.
  'platform.access',
  'platform.stripe_account.manage',
  // STGD's per-ticket fee (D-394): reserved.
  'platform.fees.manage',
] as const;
export const Permission = z.enum(PERMISSIONS);
export type Permission = z.infer<typeof Permission>;

/**
 * Permissions only STGD staff can hold (D-393): `permissions.platform_reserved = 1`.
 * No role grants them and no override can; resolution gives them to a staff
 * member signed in as an address in the Worker setting PLATFORM_STAFF_EMAILS,
 * and to nobody else. The parity test holds this list to the schema.
 */
export const PLATFORM_RESERVED_PERMISSIONS = ['platform.access', 'platform.stripe_account.manage', 'platform.fees.manage'] as const satisfies readonly Permission[];

export const RENTER_PERMISSIONS = [
  'view_sales',
  'view_inventory',
  'view_scans',
  'view_settlement',
  'download_reports',
  'manage_report_recipients',
] as const;
export const RenterPermission = z.enum(RENTER_PERMISSIONS);
export type RenterPermission = z.infer<typeof RenterPermission>;

export const SALES_CHANNELS = ['WEB', 'BOX_OFFICE', 'PHONE', 'ADMIN', 'GROUP_SALES', 'API', 'COMP', 'MIGRATION'] as const;
export const SalesChannel = z.enum(SALES_CHANNELS);
export type SalesChannel = z.infer<typeof SalesChannel>;

export const ACCOUNTING_MODES = ['BRE_PROMOTED', 'RENTAL', 'CO_PROMOTE', 'CUSTOM'] as const;
export const AccountingMode = z.enum(ACCOUNTING_MODES);
export type AccountingMode = z.infer<typeof AccountingMode>;

export const DELIVERY_METHODS = ['MOBILE', 'EMAIL_PDF', 'WALLET', 'WILL_CALL', 'BOX_OFFICE_PRINT', 'MAIL'] as const;
export const DeliveryMethod = z.enum(DELIVERY_METHODS);
export type DeliveryMethod = z.infer<typeof DeliveryMethod>;

// ---------------------------------------------------------------------------
// Events, performances, pricing configuration (Phase 3)
// ---------------------------------------------------------------------------

export const EVENT_STATUSES = ['DRAFT', 'PUBLISHED', 'CANCELLED', 'ARCHIVED'] as const;
export const EventStatus = z.enum(EVENT_STATUSES);
export type EventStatus = z.infer<typeof EventStatus>;

export const EVENT_VISIBILITIES = ['PUBLIC', 'UNLISTED', 'PRIVATE'] as const;
export const EventVisibility = z.enum(EVENT_VISIBILITIES);
export type EventVisibility = z.infer<typeof EventVisibility>;

export const PERFORMANCE_STATUSES = [
  'DRAFT',
  'SCHEDULED',
  'ON_SALE',
  'SALES_PAUSED',
  'SALES_ENDED',
  'COMPLETED',
  'CANCELLED',
  'POSTPONED',
  /** Called by the weather: every ticket became a rain check (D-374). */
  'RAINED_OUT',
] as const;
export const PerformanceStatus = z.enum(PERFORMANCE_STATUSES);
export type PerformanceStatus = z.infer<typeof PerformanceStatus>;

/** Anything that determines money is versioned: DRAFT → ACTIVE → SUPERSEDED. */
export const VERSION_STATUSES = ['DRAFT', 'ACTIVE', 'SUPERSEDED'] as const;
export const VersionStatus = z.enum(VERSION_STATUSES);
export type VersionStatus = z.infer<typeof VersionStatus>;

export const REENTRY_POLICIES = ['SINGLE_ENTRY', 'REENTRY_ALLOWED'] as const;
export const ReentryPolicy = z.enum(REENTRY_POLICIES);
export type ReentryPolicy = z.infer<typeof ReentryPolicy>;

export const PRESALE_ACCESS_TYPES = ['ACCESS_CODE', 'CUSTOMER_LIST', 'LOYALTY_VERIFIED', 'LOGGED_IN', 'PACKAGE_HOLDER'] as const;
export const PresaleAccessType = z.enum(PRESALE_ACCESS_TYPES);
export type PresaleAccessType = z.infer<typeof PresaleAccessType>;

/* Promotions (D-143). Seeded since migration 0012; these are the first
   consumers. `FEE_WAIVER` is seeded and deliberately unbuilt: a fee waiver
   cannot be expressed as cents off a ticket, which is all the pricing engine
   takes, so the API refuses it rather than pretending. */
export const PROMOTION_TYPES = ['PERCENT_OFF', 'AMOUNT_OFF', 'FIXED_PRICE', 'BUY_X_GET_Y', 'FEE_WAIVER', 'ACCESS_ONLY'] as const;
export const PromotionType = z.enum(PROMOTION_TYPES);
export type PromotionType = z.infer<typeof PromotionType>;

export const PROMOTION_STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'ENDED'] as const;
export const PromotionStatus = z.enum(PROMOTION_STATUSES);
export type PromotionStatus = z.infer<typeof PromotionStatus>;

export const PROMOTION_ELIGIBILITY_MODES = ['ANYONE', 'LOGGED_IN', 'CUSTOMER_LIST', 'LOYALTY_VERIFIED'] as const;
export const PromotionEligibilityMode = z.enum(PROMOTION_ELIGIBILITY_MODES);
export type PromotionEligibilityMode = z.infer<typeof PromotionEligibilityMode>;

export const PROMOTION_REDEMPTION_STATUSES = ['RESERVED', 'COMMITTED', 'RELEASED', 'REVERSED'] as const;
export const PromotionRedemptionStatus = z.enum(PROMOTION_REDEMPTION_STATUSES);
export type PromotionRedemptionStatus = z.infer<typeof PromotionRedemptionStatus>;

export const TICKET_TYPE_CATEGORIES = [
  'ADULT',
  'CHILD',
  'SENIOR',
  'STUDENT',
  'MILITARY',
  'VIP',
  'MEMBER',
  'GROUP',
  'PROMO',
  'COMP',
  'PACKAGE',
  'OTHER',
] as const;
export const TicketTypeCategory = z.enum(TICKET_TYPE_CATEGORIES);
export type TicketTypeCategory = z.infer<typeof TicketTypeCategory>;

export const FEE_CALCULATION_METHODS = ['FIXED_CENTS', 'PERCENT', 'FIXED_PLUS_PERCENT', 'PRICE_BANDS'] as const;
export const FeeCalculationMethod = z.enum(FEE_CALCULATION_METHODS);
export type FeeCalculationMethod = z.infer<typeof FeeCalculationMethod>;

export const FEE_APPLICATIONS = ['PER_TICKET', 'PER_ORDER'] as const;
export const FeeApplication = z.enum(FEE_APPLICATIONS);
export type FeeApplication = z.infer<typeof FeeApplication>;

export const FEE_PRICE_BASES = ['FACE_VALUE', 'SELLING_PRICE', 'ORDER_SUBTOTAL'] as const;
export const FeePriceBasis = z.enum(FEE_PRICE_BASES);
export type FeePriceBasis = z.infer<typeof FeePriceBasis>;

// ---------------------------------------------------------------------------
// Venues & manifests
// ---------------------------------------------------------------------------

/** A manifest is immutable once it leaves DRAFT; re-drawing means a new version. */
export const MANIFEST_STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'] as const;
export const ManifestStatus = z.enum(MANIFEST_STATUSES);
export type ManifestStatus = z.infer<typeof ManifestStatus>;

export const SECTION_TYPES = ['RESERVED', 'GENERAL_ADMISSION', 'TABLES', 'MIXED', 'SUITE'] as const;
export const SectionType = z.enum(SECTION_TYPES);
export type SectionType = z.infer<typeof SectionType>;

export const TABLE_SELL_MODES = ['WHOLE_TABLE', 'BY_SEAT'] as const;
export const TableSellMode = z.enum(TABLE_SELL_MODES);
export type TableSellMode = z.infer<typeof TableSellMode>;

export const GA_AREA_KINDS = ['GENERAL_ADMISSION', 'STANDING', 'LAWN', 'SUITE', 'PARKING', 'OTHER', 'TIMED_ENTRY'] as const;

/** A timed-entry slot's sellability (D-86). A closed slot still admits what it already sold. */
export const TIMED_ENTRY_SLOT_STATUSES = ['OPEN', 'SALES_STOPPED', 'CLOSED'] as const;
export type TimedEntrySlotStatus = (typeof TIMED_ENTRY_SLOT_STATUSES)[number];

/** How an arrival related to its slot's window (D-86, PART 34). */
export const TIMED_ENTRY_ARRIVAL_CLASSES = ['EARLY', 'ON_TIME', 'LATE'] as const;
export type TimedEntryArrivalClass = (typeof TIMED_ENTRY_ARRIVAL_CLASSES)[number];

/** What a room or a show offers (D-80); the lookup table carries the wording. */
export const ACCESSIBILITY_FEATURES = [
  'WHEELCHAIR_SEATING',
  /** Moving out of a chair into a seat: a thing a person needs, not a thing a building offers (D-166). */
  'WHEELCHAIR_TRANSFER',
  'STEP_FREE_ENTRANCE',
  'ELEVATOR',
  'ACCESSIBLE_PARKING',
  'ACCESSIBLE_RESTROOMS',
  'HEARING_ASSISTANCE',
  'ASL_INTERPRETED',
  'OPEN_CAPTIONS',
  'AUDIO_DESCRIPTION',
  'SENSORY_FRIENDLY',
  'SERVICE_ANIMALS',
  'LARGE_PRINT',
] as const;
export type AccessibilityFeature = (typeof ACCESSIBILITY_FEATURES)[number];
export const GaAreaKind = z.enum(GA_AREA_KINDS);
export type GaAreaKind = z.infer<typeof GaAreaKind>;

// ---------------------------------------------------------------------------
// Inventory
// ---------------------------------------------------------------------------
export const HOLD_STATES = [
  'HOUSE_HOLD',
  'ARTIST_HOLD',
  'PROMOTER_HOLD',
  'SPONSOR_HOLD',
  'PRODUCTION_HOLD',
  'VIP_HOLD',
  'CUSTOM_HOLD',
] as const;

export const RESTRICTED_POOL_STATES = ['ADA', 'COMPANION', 'RESTRICTED_VIEW'] as const;

/**
 * The pools a seat chosen by hand may be taken from (D-84).
 *
 * A wheelchair space and its companion seat are held back from best-available
 * and from general admission, so nobody is handed one by accident — but someone
 * who needs one picks it on the map and buys it like any other seat.
 */
export const CHOSEN_SEAT_STATES = ['AVAILABLE', 'ADA', 'COMPANION'] as const;

export const INVENTORY_STATES = [
  'AVAILABLE',
  'CART_HOLD',
  'PAYMENT_PENDING',
  'SOLD',
  'COMP',
  ...HOLD_STATES,
  'PRODUCTION_KILL',
  ...RESTRICTED_POOL_STATES,
  'REFUNDED',
  'EXCHANGE_HOLD',
  'DISABLED',
] as const;
export const InventoryState = z.enum(INVENTORY_STATES);
export type InventoryState = z.infer<typeof InventoryState>;
export type HoldState = (typeof HOLD_STATES)[number];
export type RestrictedPoolState = (typeof RESTRICTED_POOL_STATES)[number];

export const INVENTORY_OPERATION_TYPES = [
  'INITIALIZE',
  'COMMIT_PREPARE',
  'ORDER_COMMIT',
  'COMMIT_ABORT',
  'HOLD_PLACE',
  'HOLD_RELEASE',
  'HOLD_CONVERT',
  'KILL',
  'UNKILL',
  'POOL_ASSIGN',
  'POOL_RELEASE',
  'REFUND_RELEASE',
  'EXCHANGE_PREPARE',
  'EXCHANGE_COMMIT',
  'EXCHANGE_ABORT',
  'VOID_RELEASE',
  'PRICE_ZONE_REMAP',
  'DISABLE',
  'ENABLE',
] as const;
export const InventoryOperationType = z.enum(INVENTORY_OPERATION_TYPES);
export type InventoryOperationType = z.infer<typeof InventoryOperationType>;

// ---------------------------------------------------------------------------
// Tickets & admissions
// ---------------------------------------------------------------------------
export const TICKET_STATUSES = [
  'PENDING_PAYMENT',
  'ISSUED',
  'TRANSFER_PENDING',
  'TRANSFERRED',
  'REISSUED',
  'REFUNDED',
  'VOIDED',
  'REVOKED',
] as const;
export const TicketStatus = z.enum(TICKET_STATUSES);
export type TicketStatus = z.infer<typeof TicketStatus>;

export const SCAN_RESULTS = [
  'VALID',
  'RE_SCAN',
  'ALREADY_SCANNED',
  'INVALID',
  'WRONG_EVENT',
  'WRONG_PERFORMANCE',
  'WRONG_ACCESS_ZONE',
  'NOT_YET_VALID',
  'EXPIRED',
  'REFUNDED',
  'REVOKED',
  'TRANSFERRED',
  'MANUAL_OVERRIDE',
  'OFFLINE_ACCEPTED',
  'OFFLINE_CONFLICT',
] as const;
export const ScanResult = z.enum(SCAN_RESULTS);
export type ScanResult = z.infer<typeof ScanResult>;

export const DEVICE_TYPES = ['BOX_OFFICE_TERMINAL', 'SCANNER', 'PRINT_BRIDGE', 'KIOSK'] as const;
export const DeviceType = z.enum(DEVICE_TYPES);
export type DeviceType = z.infer<typeof DeviceType>;

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------
export const FINANCIAL_LINE_FAMILIES = ['SALE', 'CONTRA', 'COST', 'ADJUSTMENT'] as const;
export const FinancialLineFamily = z.enum(FINANCIAL_LINE_FAMILIES);
export type FinancialLineFamily = z.infer<typeof FinancialLineFamily>;

export const FINANCIAL_LINE_TYPES = [
  'TICKET_SALES',
  /** Parking, a voucher, merchandise sold beside a ticket (item 49, D-314). */
  'ADD_ON_SALE',
  /** Goods or services received in exchange for tickets (item 126, D-316). */
  'TRADE_EXPENSE',
  'TICKET_SALES_PAYABLE',
  'FACILITY_FEE',
  'PROMOTER_FEE',
  'ONLINE_CONVENIENCE_FEE',
  'ORDER_FEE',
  'DELIVERY_FEE',
  'CUSTOM_FEE',
  'PROCESSING_FEE_CHARGED',
  'TAX',
  'DONATION',
  'DISCOUNT',
  'COMP_ADJUSTMENT',
  'EXCHANGE_ADJUSTMENT',
  'EXCHANGE_FEE',
  'FLEXIBLE_TICKET_FEE',
  'PROCESSING_OFFSET',
  'CREDIT_ISSUED',
  'REFUND',
  'STRIPE_PROCESSING_COST',
  'CHARGEBACK',
  'DISPUTE_FEE',
] as const;
export const FinancialLineType = z.enum(FINANCIAL_LINE_TYPES);
export type FinancialLineType = z.infer<typeof FinancialLineType>;

export const FINANCIAL_LINE_KINDS = ['ORIGINAL', 'REVERSAL', 'ADJUSTMENT'] as const;
export const FinancialLineKind = z.enum(FINANCIAL_LINE_KINDS);
export type FinancialLineKind = z.infer<typeof FinancialLineKind>;

export const ACCOUNTING_CODES = [
  'TICKET_SALES',
  /** Revenue from add-on products (D-314). */
  'ADD_ON_SALES',
  /** The other side of a barter: what was received for the tickets (D-316). */
  'TRADE_EXPENSE',
  /** Money banked by a previous system, whose income side is already in QuickBooks (D-158). */
  'PRIOR_SYSTEM_SETTLEMENT',
  'TICKET_SALES_PAYABLE',
  'FACILITY_FEE',
  'PROMOTER_FEE',
  'PROMOTER_FEE_PAYABLE',
  'ONLINE_CONVENIENCE_FEE',
  'ORDER_FEE',
  'DELIVERY_FEE',
  'CUSTOM_FEE',
  'PROCESSING_FEE_CHARGED',
  'SALES_TAX',
  'DONATION_PAYABLE',
  'DISCOUNT',
  'COMP_ADJUSTMENT',
  'EXCHANGE_ADJUSTMENT',
  'EXCHANGE_FEE',
  'FLEXIBLE_TICKET_FEE',
  'PROCESSING_OFFSET',
  'REFUND',
  'LOYALTY_REWARD',
  'GIFT_CARD_TENDER',
  /** Credit held on customer accounts (D-372): a liability, credited when issued, debited when spent. */
  'CUSTOMER_CREDIT',
  /** The other side of a staff adjustment to account credit (D-372). */
  'CUSTOMER_CREDIT_ADJUSTMENT',
  /** Account credit that expired unspent (D-372). */
  'CUSTOMER_CREDIT_BREAKAGE',
  'STRIPE_PROCESSING_COST',
  'CHARGEBACK',
  'DISPUTE_FEE',
  'STRIPE_CLEARING',
  'CASH',
  'CHECK',
  'HOUSE_ACCOUNT_RECEIVABLE',
  'ORDER_RECEIVABLE',
  'BANK',
  'CASH_OVER_SHORT',
  'SETTLEMENT_ADJUSTMENT_INCOME',
  'SETTLEMENT_ADJUSTMENT_EXPENSE',
] as const;
export const AccountingCode = z.enum(ACCOUNTING_CODES);
export type AccountingCode = z.infer<typeof AccountingCode>;

export const REVENUE_OWNER_TYPES = [
  'BRE',
  'RENTER',
  'PROMOTER',
  'CO_PROMOTER',
  'ARTIST',
  'CHARITY',
  'TAX_AUTHORITY',
  'OTHER',
] as const;
export const RevenueOwnerType = z.enum(REVENUE_OWNER_TYPES);
export type RevenueOwnerType = z.infer<typeof RevenueOwnerType>;

export const SETTLEMENT_BEHAVIORS = [
  'PAYABLE_TO_OWNER',
  'RETAINED_BY_BRE',
  'DEDUCTED_FROM_PAYABLE',
  'PASS_THROUGH',
] as const;
export const SettlementBehavior = z.enum(SETTLEMENT_BEHAVIORS);
export type SettlementBehavior = z.infer<typeof SettlementBehavior>;

/** What an event revenue rule splits among owners (face value, or a fee whose ownership the event overrides). */
export const REVENUE_COMPONENTS = [
  'FACE_VALUE',
  'FACILITY_FEE',
  'PROMOTER_FEE',
  'ONLINE_CONVENIENCE_FEE',
  'ORDER_FEE',
  'PROCESSING_FEE',
  'DELIVERY_FEE',
  'CUSTOM_FEE',
] as const;
export const RevenueComponent = z.enum(REVENUE_COMPONENTS);
export type RevenueComponent = z.infer<typeof RevenueComponent>;

export const FEE_TYPES = [
  'FACILITY_FEE',
  'PROMOTER_FEE',
  'ONLINE_CONVENIENCE_FEE',
  'ORDER_FEE',
  'PROCESSING_FEE',
  'DELIVERY_FEE',
  'CUSTOM_FEE',
] as const;
export const FeeType = z.enum(FEE_TYPES);
export type FeeType = z.infer<typeof FeeType>;

export const TENDER_TYPES = [
  'CARD_ONLINE',
  'CARD_SAVED',
  'CARD_TERMINAL',
  'CARD_KEYED',
  'CASH',
  'CHECK',
  'GIFT_CARD',
  'LOYALTY_REWARD',
  'HOUSE_ACCOUNT',
  /** Settled in a previous ticketing system, taken over by an import (D-158). */
  'PRIOR_SYSTEM',
  /** A customer's account credit (D-372). */
  'ACCOUNT_CREDIT',
] as const;
export const TenderType = z.enum(TENDER_TYPES);
export type TenderType = z.infer<typeof TenderType>;

/**
 * What a line of a customer's account credit is (D-372). The balance is the
 * sum of these; nothing stores it. Mirrors `customer_credit_entry_kinds`.
 */
export const CUSTOMER_CREDIT_ENTRY_KINDS = ['ISSUED', 'REDEEMED', 'REVERSED', 'ADJUSTED', 'EXPIRED'] as const;
export const CustomerCreditEntryKind = z.enum(CUSTOMER_CREDIT_ENTRY_KINDS);
export type CustomerCreditEntryKind = z.infer<typeof CustomerCreditEntryKind>;

/**
 * What a rained-out ticket became (D-374). Mirrors `rain_check_kinds`: account
 * credit for what was paid, or nothing to give back for a comp, a season pass
 * (it covers the makeup) and a voucher (a replacement is issued instead).
 */
export const RAIN_CHECK_KINDS = ['CREDIT', 'COMP', 'SEASON_PASS', 'VOUCHER'] as const;
export const RainCheckKind = z.enum(RAIN_CHECK_KINDS);
export type RainCheckKind = z.infer<typeof RainCheckKind>;

/**
 * Group booking management (D-376). Each list mirrors a lookup table seeded in
 * migration 0121; packages/domain/src/parity.test.ts holds them together.
 */
/** What a group books: a space whole for a game, a package, or a block of tickets at a group rate. */
export const GROUP_PRODUCT_KINDS = ['SPACE', 'PACKAGE', 'TICKET_BLOCK'] as const;
export const GroupProductKind = z.enum(GROUP_PRODUCT_KINDS);
export type GroupProductKind = z.infer<typeof GroupProductKind>;

/** What kind of group is asking; the inquiry form's choices. */
export const GROUP_TYPES = ['BIRTHDAY', 'BUSINESS', 'TEAM_LEAGUE', 'SCHOOL', 'CHURCH_NONPROFIT', 'OTHER'] as const;
export const GroupType = z.enum(GROUP_TYPES);
export type GroupType = z.infer<typeof GroupType>;

/** Where a lead came from. */
export const GROUP_LEAD_SOURCES = ['WEB_FORM', 'PHONE', 'EMAIL', 'WALK_IN', 'REPEAT', 'REFERRAL', 'OTHER'] as const;
export const GroupLeadSource = z.enum(GROUP_LEAD_SOURCES);
export type GroupLeadSource = z.infer<typeof GroupLeadSource>;

/** The pipeline. The allowed moves are GROUP_LEAD_TRANSITIONS in packages/domain. */
export const GROUP_LEAD_STATUSES = ['NEW', 'CONTACTED', 'QUOTED', 'HOLD', 'BOOKED', 'COMPLETED', 'LOST', 'CANCELLED'] as const;
export const GroupLeadStatus = z.enum(GROUP_LEAD_STATUSES);
export type GroupLeadStatus = z.infer<typeof GroupLeadStatus>;

/** What a line of a lead's timeline records. */
export const GROUP_LEAD_ACTIVITY_KINDS = [
  'INQUIRY',
  'NOTE',
  'CALL',
  'STATUS_CHANGED',
  'ASSIGNED',
  'FOLLOW_UP_SET',
  'DETAILS_CHANGED',
  'EMAIL_SENT',
  'QUOTE_CREATED',
  'QUOTE_SENT',
  'QUOTE_ACCEPTED',
  'HOLD_PLACED',
  'HOLD_RELEASED',
  'BOOKED',
  'BOOKING_CANCELLED',
  'PAID',
] as const;
export const GroupLeadActivityKind = z.enum(GROUP_LEAD_ACTIVITY_KINDS);
export type GroupLeadActivityKind = z.infer<typeof GroupLeadActivityKind>;

export const GROUP_QUOTE_STATUSES = ['DRAFT', 'SENT', 'ACCEPTED', 'SUPERSEDED', 'DECLINED'] as const;
export const GroupQuoteStatus = z.enum(GROUP_QUOTE_STATUSES);
export type GroupQuoteStatus = z.infer<typeof GroupQuoteStatus>;

export const GROUP_QUOTE_LINE_KINDS = ['PRODUCT', 'OPTION', 'INCLUDED_TICKETS', 'TICKETS', 'DISCOUNT'] as const;
export const GroupQuoteLineKind = z.enum(GROUP_QUOTE_LINE_KINDS);
export type GroupQuoteLineKind = z.infer<typeof GroupQuoteLineKind>;

/** HELD and BOOKED keep the space (`claims_space`); RELEASED and CANCELLED have let it go. */
export const GROUP_BOOKING_STATUSES = ['HELD', 'BOOKED', 'RELEASED', 'CANCELLED'] as const;
export const GroupBookingStatus = z.enum(GROUP_BOOKING_STATUSES);
export type GroupBookingStatus = z.infer<typeof GroupBookingStatus>;

/** Stripe Connect (D-391, D-393): what a connected account is to this deployment. HOUSE is the team selling tickets. */
export const STRIPE_CONNECTED_ACCOUNT_ROLES = ['HOUSE'] as const;
export const StripeConnectedAccountRole = z.enum(STRIPE_CONNECTED_ACCOUNT_ROLES);
export type StripeConnectedAccountRole = z.infer<typeof StripeConnectedAccountRole>;

/** Where a connected account's onboarding stands, from Stripe's `charges_enabled`, `payouts_enabled` and requirements (D-393). */
export const STRIPE_CONNECTED_ACCOUNT_STATUSES = ['ONBOARDING', 'RESTRICTED', 'ENABLED', 'DISABLED', 'DISCONNECTED'] as const;
export const StripeConnectedAccountStatus = z.enum(STRIPE_CONNECTED_ACCOUNT_STATUSES);
export type StripeConnectedAccountStatus = z.infer<typeof StripeConnectedAccountStatus>;

/** How a connected account was connected: a new one through Account Links, or an existing one through OAuth (D-393). */
export const STRIPE_ACCOUNT_CONNECTION_METHODS = ['OAUTH', 'ACCOUNT_LINK'] as const;
export const StripeAccountConnectionMethod = z.enum(STRIPE_ACCOUNT_CONNECTION_METHODS);
export type StripeAccountConnectionMethod = z.infer<typeof StripeAccountConnectionMethod>;

/**
 * The Promotions library (D-377): the icon a promotion is drawn with, the
 * schedule chip it lights up, what each of its offers is, and how a game came
 * to be linked to it. Lookup tables in migration 0123.
 */
export const GAME_PROMOTION_ICONS = ['STAR', 'FIREWORKS', 'DOG', 'BEER', 'HOT_DOG', 'TICKET', 'FAMILY', 'JERSEY', 'GIFT', 'MUSIC', 'TROPHY', 'HEART'] as const;
export const GamePromotionIcon = z.enum(GAME_PROMOTION_ICONS);
export type GamePromotionIcon = z.infer<typeof GamePromotionIcon>;

export const GAME_PROMOTION_CHIPS = ['PROMOTIONS', 'FIREWORKS', 'THEME_NIGHTS', 'FAMILY_FUN', 'GIVEAWAYS', 'SPECIALS'] as const;
export const GamePromotionChip = z.enum(GAME_PROMOTION_CHIPS);
export type GamePromotionChip = z.infer<typeof GamePromotionChip>;

export const GAME_PROMOTION_OFFER_KINDS = ['INFO', 'TICKET_DEAL', 'CONCESSION_SPECIAL', 'GIVEAWAY'] as const;
export const GamePromotionOfferKind = z.enum(GAME_PROMOTION_OFFER_KINDS);
export type GamePromotionOfferKind = z.infer<typeof GamePromotionOfferKind>;

export const GAME_PROMOTION_LINK_SOURCES = ['RULE', 'MANUAL'] as const;
export const GamePromotionLinkSource = z.enum(GAME_PROMOTION_LINK_SOURCES);
export type GamePromotionLinkSource = z.infer<typeof GamePromotionLinkSource>;

export const ORDER_STATUSES = [
  'PENDING',
  'MANUAL_REVIEW',
  'COMPLETED',
  'PARTIALLY_REFUNDED',
  'REFUNDED',
  'CANCELLED',
  'EXPIRED',
] as const;
export const OrderStatus = z.enum(ORDER_STATUSES);
export type OrderStatus = z.infer<typeof OrderStatus>;

export const PAYMENT_PLAN_STATUSES = [
  'QUOTED',
  'RESERVING',
  'RESERVED',
  'COMMITTING',
  'COMMITTED',
  'RELEASING',
  'RELEASED',
  'MANUAL_REVIEW',
] as const;
export const PaymentPlanStatus = z.enum(PAYMENT_PLAN_STATUSES);
export type PaymentPlanStatus = z.infer<typeof PaymentPlanStatus>;

export const PAYMENT_STATUSES = [
  'PENDING',
  'RESERVING',
  'AWAITING_CONFIRMATION',
  'RESERVED',
  'CAPTURING',
  'CAPTURED',
  'RELEASING',
  'RELEASED',
  'FAILED',
  'MANUAL_REVIEW',
] as const;
export const PaymentStatus = z.enum(PAYMENT_STATUSES);
export type PaymentStatus = z.infer<typeof PaymentStatus>;

export const REFUND_STATUSES = [
  'REQUESTED',
  'APPROVED',
  'PROCESSING',
  'COMPLETED',
  'PARTIALLY_COMPLETED',
  'FAILED',
  'REJECTED',
] as const;
export const RefundStatus = z.enum(REFUND_STATUSES);
export type RefundStatus = z.infer<typeof RefundStatus>;

// ---------------------------------------------------------------------------
// Messaging & reporting
// ---------------------------------------------------------------------------
export const OUTBOX_DESTINATIONS = ['EVENTS', 'LOYALTY', 'EMAIL', 'REPORTS', 'ARTIFACTS', 'PRINT', 'RECONCILIATION'] as const;
export const OutboxDestination = z.enum(OUTBOX_DESTINATIONS);
export type OutboxDestination = z.infer<typeof OutboxDestination>;

export const REPORT_TYPES = [
  'EVENT_AUDIT',
  'DAILY_SALES',
  'SALES_BY_EVENT',
  'SALES_BY_PERFORMANCE',
  'SALES_BY_TICKET_TYPE',
  'SALES_BY_PRICE',
  'SALES_BY_PRICE_LEVEL',
  'SALES_BY_SECTION',
  'SALES_BY_PROMO_CODE',
  'SALES_BY_CHANNEL',
  'SALES_BY_SOURCE',
  'SALES_BY_REGISTER',
  'SALES_BY_EMPLOYEE',
  'SALES_BY_PAYMENT_METHOD',
  'SALES_BY_CARD_BRAND',
  'DELIVERY_SUMMARY',
  'TICKET_PRICE_SUMMARY',
  'FEE_SUMMARY',
  'FACILITY_FEE',
  'PROMOTER_FEE',
  'ONLINE_CONVENIENCE_FEE',
  'PROCESSING_FEE',
  'DISCOUNT',
  'COMP',
  'REFUND',
  'EXCHANGE',
  'CHARGEBACK',
  'INVENTORY',
  'HOLD',
  'UNSOLD_INVENTORY',
  'SCAN_GATE_CONTROL',
  'SCAN_SUMMARY',
  'ATTENDANCE',
  'NO_SHOW',
  'RENTER_SETTLEMENT',
  'ACCOUNTING_SUMMARY',
  'DEFERRED_REVENUE',
  'CASH_DRAWER',
  'STRIPE_RECONCILIATION',
  'STRIPE_PAYOUT_DETAIL',
  'STRIPE_FEE_DETAIL',
  'STRIPE_CLEARING_BALANCE',
  'UNMATCHED_STRIPE_TRANSACTIONS',
  'QBO_SYNC_STATUS',
  'QBO_POSTING_ERRORS',
  'CASH_DRAWER_RECONCILIATION',
  'GIFT_CARD_TENDER_RECONCILIATION',
  'RENTER_PAYABLE_RECONCILIATION',
  'HOUSE_ACCOUNT_STATEMENT',
  'CUSTOMER_SUMMARY',
  'PACKAGE_HOLDERS',
  'PAYMENT_METHOD_SUMMARY',
  'PAYMENT_METHOD_FEES',
  'BNPL_USAGE',
  'PARTIAL_PAYMENTS',
  'OUTSTANDING_BALANCES',
  'PAYMENT_REQUESTS',
  'PAST_DUE_ORDERS',
  'PAYMENTS_RECEIVED',
  'TIMED_ENTRY_SALES',
  'TIMED_ENTRY_ADMISSION',
  /*
   * Knowing what worked (phase 5, D-301). The funnel D-194 wired went to GA4
   * and Meta; these answer the same questions from `carts` and `orders`, for
   * every buyer rather than the share who load tags.
   */
  'CART_ABANDONMENT',
  'CONVERSION_BY_EVENT',
  'ON_SALE_CURVE',
  'SEAT_DEMAND',
  'RETURNING_CUSTOMERS',
  'PROMO_CODE_IMPACT',
  // The Promotions library (D-377): per promotion, and its nights against the others.
  'PROMOTION_NIGHTS',
  'PROMOTION_NIGHT_COMPARISON',
  'CAMPAIGN_RETURN', 'BANK_DEPOSITS',
  // Flexible tickets (D-197) and year-end donor statements (D-201).
  'FLEXIBLE_CREDIT',
  'FLEXIBLE_TAKEUP',
  /** What the venue owes customers in account credit, in total and per customer (D-372). */
  'OUTSTANDING_CUSTOMER_CREDIT',
  'DONOR_STATEMENT',
] as const;
export const ReportType = z.enum(REPORT_TYPES);
export type ReportType = z.infer<typeof ReportType>;

// --- Integrations (D-50) ------------------------------------------------------

export const INTEGRATION_MODES = ['TEST', 'LIVE'] as const;
export const IntegrationMode = z.enum(INTEGRATION_MODES);
export type IntegrationMode = z.infer<typeof IntegrationMode>;

export interface IntegrationField {
  key: string;
  label: string;
  /** Secrets are never read back; public values (publishable keys, site keys) are. */
  secret: boolean;
  hint?: string;
}

/**
 * The providers the admin Settings page knows how to hold keys for. Each is
 * stored per mode (test / live) and the active mode is switched per provider.
 */
export const INTEGRATION_PROVIDERS = {
  STRIPE: {
    name: 'Stripe',
    description: 'Card payments for tickets and donations. Ticketing has its own Stripe account (D-01).',
    fields: [
      { key: 'publishable_key', label: 'Publishable key', secret: false, hint: 'pk_test_… or pk_live_…' },
      { key: 'secret_key', label: 'Secret key', secret: true, hint: 'sk_test_… or sk_live_… (restricted keys rk_… also work)' },
      { key: 'webhook_secret', label: 'Webhook signing secret', secret: true, hint: 'whsec_… from the endpoint for /api/webhooks/stripe' },
    ],
  },
  TURNSTILE: {
    name: 'Cloudflare Turnstile',
    description: 'Bot check on seat holds and donations.',
    fields: [
      { key: 'site_key', label: 'Site key', secret: false },
      { key: 'secret_key', label: 'Secret key', secret: true },
    ],
  },
  GOOGLE_WALLET: {
    name: 'Google Wallet',
    description: '"Add to Google Wallet" passes for tickets. Needs a Google Pay & Wallet Console issuer and a service account with the Wallet API enabled (Q-10).',
    fields: [
      { key: 'issuer_id', label: 'Issuer ID', secret: false, hint: 'the numeric issuer id from the Google Wallet console' },
      { key: 'service_account_email', label: 'Service account email', secret: false, hint: '…@…iam.gserviceaccount.com (optional when the key below is the whole JSON file)' },
      { key: 'service_account_key', label: 'Service account private key', secret: true, hint: 'the private_key value, or paste the entire service-account JSON file' },
    ],
  },
  APPLE_WALLET: {
    name: 'Apple Wallet',
    description:
      'Passes for iPhone, alongside the Google ones (D-246). Needs a Pass Type ID certificate from an Apple Developer account, its private key, and Apple’s WWDR intermediate. Export the certificate and key from the .p12 as PEM: `openssl pkcs12 -in pass.p12 -clcerts -nokeys -out cert.pem` and `openssl pkcs12 -in pass.p12 -nocerts -nodes | openssl pkcs8 -topk8 -nocrypt -out key.pem`.',
    fields: [
      { key: 'pass_type_identifier', label: 'Pass Type ID', secret: false, hint: 'pass.com.example.tickets, exactly as registered' },
      { key: 'team_identifier', label: 'Team ID', secret: false, hint: 'the ten-character team id from the Apple Developer account' },
      { key: 'certificate_pem', label: 'Pass certificate (PEM)', secret: true, hint: 'the -----BEGIN CERTIFICATE----- block for the Pass Type ID' },
      { key: 'private_key_pem', label: 'Private key (PKCS#8 PEM)', secret: true, hint: 'the -----BEGIN PRIVATE KEY----- block; an encrypted key will not work' },
      { key: 'wwdr_pem', label: 'Apple WWDR intermediate (PEM)', secret: true, hint: 'downloaded from Apple; without it the chain is incomplete and the pass is refused' },
    ],
  },
  ANALYTICS: {
    name: 'Website analytics',
    description: 'Measurement for the storefront (D-98) and server-side purchases (D-202). The two IDs are public identifiers that appear in the page source; the two secrets below are not, and they let a completed order be reported from the server as well as the browser, de-duplicated on the order number. Leave a field empty and that half is simply not used.',
    fields: [
      { key: 'ga4_measurement_id', label: 'GA4 measurement ID', secret: false, hint: 'G-XXXXXXXXXX' },
      { key: 'meta_pixel_id', label: 'Meta pixel ID', secret: false, hint: 'the numeric pixel id from Events Manager' },
      { key: 'ga4_api_secret', label: 'GA4 Measurement Protocol API secret', secret: true, hint: 'optional; enables server-side purchase events alongside the browser tag' },
      { key: 'meta_access_token', label: 'Meta Conversions API access token', secret: true, hint: 'optional; enables server-side purchase events alongside the pixel' },
    ],
  },
} as const satisfies Record<string, { name: string; description: string; fields: readonly IntegrationField[] }>;

export const INTEGRATION_PROVIDER_CODES = Object.keys(INTEGRATION_PROVIDERS) as (keyof typeof INTEGRATION_PROVIDERS)[];
export const IntegrationProvider = z.enum(INTEGRATION_PROVIDER_CODES as [keyof typeof INTEGRATION_PROVIDERS, ...(keyof typeof INTEGRATION_PROVIDERS)[]]);
export type IntegrationProvider = z.infer<typeof IntegrationProvider>;

// --- Donations (D-51) ---------------------------------------------------------

// PLEDGE: one payment against a pledge (D-356), a gift like any other once paid.
export const DONATION_KINDS = ['ONE_TIME', 'RECURRING', 'CHECKOUT', 'PLEDGE'] as const;
export const DonationKind = z.enum(DONATION_KINDS);
export type DonationKind = z.infer<typeof DonationKind>;

export const DONATION_STATUSES = ['PENDING', 'SUCCEEDED', 'FAILED', 'ACTIVE', 'PAST_DUE', 'PAUSED', 'CANCELED', 'ENDED', 'REFUNDED'] as const;
export const DonationStatus = z.enum(DONATION_STATUSES);
export type DonationStatus = z.infer<typeof DonationStatus>;

export const DONATION_INTERVALS = ['MONTH', 'YEAR'] as const;
export const DonationInterval = z.enum(DONATION_INTERVALS);
export type DonationInterval = z.infer<typeof DonationInterval>;

// ---------------------------------------------------------------------------
// Balances, payment requests and payment methods (D-71, docs/payment-requests.md)
// ---------------------------------------------------------------------------
export const ORDER_PAYMENT_STATUSES = ['UNPAID', 'PARTIALLY_PAID', 'PAID', 'OVERPAID', 'REFUND_PENDING', 'PARTIALLY_REFUNDED', 'REFUNDED', 'PAYMENT_FAILED', 'VOID'] as const;
export const OrderPaymentStatus = z.enum(ORDER_PAYMENT_STATUSES);
export type OrderPaymentStatus = z.infer<typeof OrderPaymentStatus>;

export const TICKET_ISSUANCE_POLICIES = ['ISSUE_IMMEDIATELY', 'ISSUE_WHEN_PAID', 'ISSUE_BUT_DISABLE_ADMISSION_UNTIL_PAID'] as const;
export const TicketIssuancePolicy = z.enum(TICKET_ISSUANCE_POLICIES);

export const PAST_DUE_POLICIES = ['NO_AUTOMATIC_ACTION', 'NOTIFY_STAFF', 'AUTO_CANCEL_AFTER_GRACE_PERIOD'] as const;
export const PastDuePolicy = z.enum(PAST_DUE_POLICIES);

export const PAYMENT_REQUEST_STATUSES = ['DRAFT', 'ACTIVE', 'PARTIALLY_PAID', 'PAID', 'EXPIRED', 'REVOKED', 'SUPERSEDED'] as const;
export const PaymentRequestStatus = z.enum(PAYMENT_REQUEST_STATUSES);

export const PAYMENT_REQUEST_AMOUNT_POLICIES = ['FULL_REMAINING_BALANCE_ONLY', 'AT_LEAST_MINIMUM', 'CUSTOM_AMOUNT_ALLOWED', 'FIXED_REQUEST_AMOUNT'] as const;
export const PaymentRequestAmountPolicy = z.enum(PAYMENT_REQUEST_AMOUNT_POLICIES);

export const PAYMENT_PROCESSORS = ['STRIPE', 'CASH', 'CHECK', 'HOUSE_ACCOUNT', 'GIFT_CARD', 'LOYALTY', 'COMP', 'OTHER'] as const;
export const PAYMENT_METHOD_FAMILIES = ['CARD', 'WALLET', 'BNPL', 'BANK', 'OTHER'] as const;
export const PAYMENT_METHODS = ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER', 'DINERS', 'JCB', 'UNIONPAY', 'APPLE_PAY', 'GOOGLE_PAY', 'KLARNA', 'AFFIRM', 'AFTERPAY', 'LINK', 'CASH_APP', 'US_BANK_ACCOUNT', 'CASH', 'CHECK', 'HOUSE_ACCOUNT', 'GIFT_CARD', 'LOYALTY', 'UNKNOWN'] as const;
export const PaymentMethod = z.enum(PAYMENT_METHODS);


// ---------------------------------------------------------------------------
// Gift tickets delivered on a chosen date (D-353)
// ---------------------------------------------------------------------------
export const GIFT_DELIVERY_STATUSES = ['SCHEDULED', 'SENDING', 'SENT', 'CANCELED', 'NOTHING_TO_SEND'] as const;
export const GiftDeliveryStatus = z.enum(GIFT_DELIVERY_STATUSES);
export type GiftDeliveryStatus = z.infer<typeof GiftDeliveryStatus>;

// ---------------------------------------------------------------------------
// Passes: a D-63 membership pass and a D-373 season pass share the lookup.
// REVOKED (0118) is a season pass taken away for good; its QR never scans again.
// ---------------------------------------------------------------------------
export const PASS_STATUSES = ['ACTIVE', 'SUSPENDED', 'EXPIRED', 'CANCELLED', 'REVOKED'] as const;
export const PassStatus = z.enum(PASS_STATUSES);
export type PassStatus = z.infer<typeof PassStatus>;

// ---------------------------------------------------------------------------
// Sales history from the previous system (D-364)
// ---------------------------------------------------------------------------
export const PRIOR_SYSTEM_LINE_KINDS = ['TICKET', 'GIFT', 'SURCHARGE', 'OTHER'] as const;
export const PriorSystemLineKind = z.enum(PRIOR_SYSTEM_LINE_KINDS);
export type PriorSystemLineKind = z.infer<typeof PriorSystemLineKind>;
